/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Registry of category types that have a "groups" concept — categories whose
 * docs are organised under named entities (vehicles, properties, pets,
 * family members, ...) rather than a flat list.
 *
 * All 13 group-capable categories live here. Vehicle and Property keep their
 * dedicated services (DVLA lookup, embedded docs, etc.); the other 11 use
 * the shared `groupsApi` and follow the same backend conventions.
 *
 * Lookup keys: category SLUG (preferred — stable backend identifier) or
 * numeric category id (legacy — only known for Vehicle and Property).
 * Each entry knows:
 *   - apiPath / apiPathSingular: backend route segments
 *   - docFkField: the FK column on the documents table for this type
 *   - schemaKey: matches a key in groupFormSchemas
 *   - addPath: where the legacy add-flow used to navigate (kept for
 *     back-compat; modal-driven adds use the schema directly)
 *   - toCardData(raw): maps an API record to DocumentGroupCard props
 *   - navigateToGroup(raw, cat): builds the /documents/viewGroup target
 *   - isDocOrphan(doc): whether a doc with no FK is "orphan" for this type
 */
import {
  vehicleId, propertyId, linkPath, getDvlaColorHex,
} from 'common/constants';
import { VehicleListElement } from 'features/Vehicle/types';
import { PropertyListElement } from 'features/Property/types';

export type GroupCardData = {
  id: number;
  title: string;
  subLabel?: string;
  doqsCount: number;
  isShared?: boolean;
  iconName?: string;
  iconColor?: string;
};

export type GroupNavTarget = {
  path: string;
  state: Record<string, unknown>;
};

// Doc records use a polymorphic shape — any of these FK columns might be
// present depending on which group type the doc is attached to.
export type DocLike = {
  vehicle_id?: number | null;
  property_id?: number | null;
  pet_id?: number | null;
  family_member_id?: number | null;
  health_person_id?: number | null;
  identity_person_id?: number | null;
  education_person_id?: number | null;
  trip_id?: number | null;
  pension_account_id?: number | null;
  subscription_id?: number | null;
  bank_account_id?: number | null;
  job_id?: number | null;
  tax_period_id?: number | null;
  custom_group_id?: number | null;
};

export type GroupCategoryConfig<TGroup = any> = {
  /** Stable category identifier from the backend categories table. */
  categorySlug: string;
  /** Optional numeric category id when known statically (vehicle = 11,
   *  property = 7); other categories' ids are read at runtime via the
   *  category response. */
  categoryId?: number;

  /** Backend route segment for CRUD (plural). e.g. 'pets', 'family-members'. */
  apiPath: string;
  /** Singular form used on share endpoints. e.g. 'pet', 'family-member'. */
  apiPathSingular: string;
  /** FK column on documents that links to this group type. */
  docFkField: keyof DocLike;
  /** Key into groupFormSchemas. */
  schemaKey: string;

  /** Where the "Add a X" CTA navigates when modal-add isn't used. Mostly
   *  legacy — modal-add resolves directly from schemaKey. */
  addPath?: string;

  /** Maps a raw API record to DocumentGroupCard props. */
  toCardData: (group: TGroup) => GroupCardData;

  /** Builds the /documents/viewGroup destination. */
  navigateToGroup: (
    group: TGroup,
    categoryInformation: Record<string, unknown>,
  ) => GroupNavTarget;

  /** True when this doc isn't attached to a group of this type. */
  isDocOrphan: (doc: DocLike) => boolean;

  /** Default icon-set key for cards. */
  defaultIcon: string;
  /** Default brand color. */
  defaultColor: string;
};

// --- Vehicle (legacy, dedicated service) ------------------------------------

const resolveVehicleColor = (v: VehicleListElement): string => {
  if (v.custom_color) return v.custom_color;
  if (v.color) return getDvlaColorHex(v.color);
  return '#9333EA';
};

const vehicleConfig: GroupCategoryConfig<VehicleListElement> = {
  categorySlug: 'vehicle',
  categoryId: vehicleId,
  apiPath: 'vehicles',
  apiPathSingular: 'vehicle',
  docFkField: 'vehicle_id',
  schemaKey: 'vehicle',
  addPath: linkPath.addVehicle,
  defaultIcon: 'Vehicle',
  defaultColor: '#04BFBB',
  toCardData: (v) => ({
    id: v.id,
    title: v.name || v.number || `Vehicle ${v.id}`,
    subLabel: v.number && v.name ? v.number : undefined,
    doqsCount: v.documents_count || 0,
    isShared: (v.shares_count || 0) > 0,
    iconName: 'Vehicle',
    iconColor: resolveVehicleColor(v),
  }),
  navigateToGroup: (v, categoryInformation) => ({
    path: `${linkPath.viewGroupPath}/vehicle/${v.id}`,
    state: { group: v, categoryInformation },
  }),
  isDocOrphan: (doc) => !doc.vehicle_id,
};

// --- Property (legacy, dedicated service) -----------------------------------

const propertyConfig: GroupCategoryConfig<PropertyListElement> = {
  categorySlug: 'property',
  categoryId: propertyId,
  apiPath: 'properties',
  apiPathSingular: 'property',
  docFkField: 'property_id',
  schemaKey: 'property',
  addPath: linkPath.addProperty,
  defaultIcon: 'Property',
  defaultColor: '#FFA683',
  toCardData: (p) => ({
    id: p.id,
    title: p.name,
    subLabel: (p as PropertyListElement & { address?: string }).address,
    doqsCount: p.documents_count || 0,
    isShared: (p.shares_count || 0) > 0,
    iconName: p.icon || 'Property',
    iconColor: p.color || '#9333EA',
  }),
  navigateToGroup: (p, categoryInformation) => ({
    path: `${linkPath.viewGroupPath}/property/${p.id}`,
    state: { group: p, categoryInformation },
  }),
  isDocOrphan: (doc) => !doc.property_id,
};

// --- Generic config builder for the new categories --------------------------
// They all share the same shape — universal fields plus a few type-specific
// extras. The default toCardData uses .name as the title; type-specific
// builders can override below if they want a richer card label.

type GenericGroupRaw = {
  id: number;
  name?: string;
  icon?: string;
  color?: string;
  documents_count?: number;
  shares_count?: number;
  // Stamped by the API when this record reached the list through a share
  // rather than being owned by the caller.
  is_shared_with_me?: boolean;
  owner?: { id: number; display_name?: string; profile_picture?: string } | null;
  // type-specifics flow through as `any` since the registry treats them
  // opaquely; ViewGroup pages handle the bespoke rendering.
  [key: string]: any;
};

const genericConfig = (overrides: {
  categorySlug: string;
  apiPath: string;
  apiPathSingular: string;
  docFkField: keyof DocLike;
  schemaKey: string;
  defaultIcon: string;
  defaultColor: string;
  buildSubLabel?: (raw: GenericGroupRaw) => string | undefined;
  groupTypeForRoute?: string; // /documents/viewGroup/:groupType/:id — defaults to apiPathSingular
}): GroupCategoryConfig<GenericGroupRaw> => ({
  categorySlug: overrides.categorySlug,
  apiPath: overrides.apiPath,
  apiPathSingular: overrides.apiPathSingular,
  docFkField: overrides.docFkField,
  schemaKey: overrides.schemaKey,
  defaultIcon: overrides.defaultIcon,
  defaultColor: overrides.defaultColor,
  toCardData: (raw) => ({
    id: raw.id,
    // Groups shared WITH this user carry is_shared_with_me + owner from the
    // API. Label them by owner like the shared vehicle/property cards do,
    // rather than letting them look like the user's own.
    title: raw.name || `Group ${raw.id}`,
    subLabel: raw.is_shared_with_me && raw.owner?.display_name
      ? `Shared by ${raw.owner.display_name}`
      : overrides.buildSubLabel?.(raw),
    doqsCount: raw.documents_count || 0,
    isShared: (raw.shares_count || 0) > 0 || !!raw.is_shared_with_me,
    iconName: raw.icon || overrides.defaultIcon,
    iconColor: raw.color || overrides.defaultColor,
  }),
  navigateToGroup: (raw, categoryInformation) => ({
    path: `${linkPath.viewGroupPath}/${overrides.groupTypeForRoute || overrides.apiPathSingular}/${raw.id}`,
    state: {
      group: raw,
      categoryInformation,
      // isSharedIn (not isShared): the recipient IS authorised on the detail
      // endpoint, so ViewGroup still fetches the docs - this only hides the
      // owner-only actions.
      ...(raw.is_shared_with_me
        ? { isSharedIn: true, sharedOwner: raw.owner }
        : {}),
    },
  }),
  isDocOrphan: (doc) => !doc[overrides.docFkField],
});

// --- All 11 new group-capable categories ------------------------------------

const petConfig = genericConfig({
  categorySlug: 'pets',
  apiPath: 'pets',
  apiPathSingular: 'pet',
  docFkField: 'pet_id',
  schemaKey: 'pet',
  defaultIcon: 'Pets',
  defaultColor: '#D4A373',
  buildSubLabel: (p) => p.species || undefined,
});

const familyConfig = genericConfig({
  categorySlug: 'family',
  apiPath: 'family-members',
  apiPathSingular: 'family-member',
  docFkField: 'family_member_id',
  schemaKey: 'family-member',
  defaultIcon: 'Family',
  defaultColor: '#C474FF',
  buildSubLabel: (m) => m.relationship || undefined,
});

const healthConfig = genericConfig({
  categorySlug: 'health',
  apiPath: 'health-people',
  apiPathSingular: 'health-person',
  docFkField: 'health_person_id',
  schemaKey: 'health-person',
  defaultIcon: 'Health',
  defaultColor: '#E99A8B',
});

const identityConfig = genericConfig({
  categorySlug: 'identity',
  apiPath: 'identity-people',
  apiPathSingular: 'identity-person',
  docFkField: 'identity_person_id',
  schemaKey: 'identity-person',
  defaultIcon: 'Identity',
  defaultColor: '#74BBDC',
});

const educationConfig = genericConfig({
  categorySlug: 'education',
  apiPath: 'education-people',
  apiPathSingular: 'education-person',
  docFkField: 'education_person_id',
  schemaKey: 'education-person',
  defaultIcon: 'Education',
  defaultColor: '#6DBE6D',
  buildSubLabel: (p) => p.current_institution || undefined,
});

const tripConfig = genericConfig({
  categorySlug: 'travel',
  apiPath: 'trips',
  apiPathSingular: 'trip',
  docFkField: 'trip_id',
  schemaKey: 'trip',
  defaultIcon: 'Travel',
  defaultColor: '#FDAE41',
  buildSubLabel: (t) => t.destination || undefined,
});

const pensionConfig = genericConfig({
  categorySlug: 'pension',
  apiPath: 'pension-accounts',
  apiPathSingular: 'pension-account',
  docFkField: 'pension_account_id',
  schemaKey: 'pension-account',
  defaultIcon: 'Pension',
  defaultColor: '#BD63BC',
  buildSubLabel: (p) => p.provider || undefined,
});

const subscriptionConfig = genericConfig({
  categorySlug: 'subscriptions',
  apiPath: 'subscriptions',
  apiPathSingular: 'subscription-group',
  // Note: backend uses subscription_id (short form) NOT subscription_group_id.
  docFkField: 'subscription_id',
  schemaKey: 'subscription',
  defaultIcon: 'Subscriptions',
  defaultColor: '#32AFE4',
  buildSubLabel: (s) => s.service_name || undefined,
});

const bankConfig = genericConfig({
  categorySlug: 'bank',
  apiPath: 'bank-accounts',
  apiPathSingular: 'bank-account',
  docFkField: 'bank_account_id',
  schemaKey: 'bank-account',
  defaultIcon: 'Bank',
  defaultColor: '#1ABCE1',
  buildSubLabel: (b) => b.bank_name || undefined,
});

const jobConfig = genericConfig({
  categorySlug: 'work',
  apiPath: 'jobs',
  apiPathSingular: 'job',
  docFkField: 'job_id',
  schemaKey: 'job',
  defaultIcon: 'Work',
  defaultColor: '#94B848',
  buildSubLabel: (j) => j.company || undefined,
});

const taxPeriodConfig = genericConfig({
  categorySlug: 'tax',
  apiPath: 'tax-periods',
  apiPathSingular: 'tax-period',
  docFkField: 'tax_period_id',
  schemaKey: 'tax-period',
  defaultIcon: 'Tax',
  defaultColor: '#EC4A85',
  buildSubLabel: (t) => t.tax_year || undefined,
});

const customGroupConfig = genericConfig({
  categorySlug: 'custom',
  apiPath: 'custom-category-groups',
  apiPathSingular: 'custom-category-group',
  // Note: backend uses custom_group_id (short form).
  docFkField: 'custom_group_id',
  schemaKey: 'custom-group',
  defaultIcon: 'CustomFolder',
  defaultColor: '#9333EA',
});

// --- Registry ---------------------------------------------------------------

const ALL_CONFIGS: GroupCategoryConfig[] = [
  vehicleConfig, propertyConfig,
  petConfig, familyConfig, healthConfig, identityConfig, educationConfig,
  tripConfig, pensionConfig, subscriptionConfig, bankConfig, jobConfig,
  taxPeriodConfig, customGroupConfig,
];

// Slug → config (primary lookup; slugs are stable across the backend).
const BY_SLUG: Record<string, GroupCategoryConfig> = ALL_CONFIGS.reduce(
  (acc, cfg) => ({ ...acc, [cfg.categorySlug]: cfg }),
  {},
);

// Numeric category id → config (only populated for vehicle/property today;
// other categories' ids are looked up via category data at runtime).
export const GROUP_REGISTRY: Record<number, GroupCategoryConfig> = ALL_CONFIGS.reduce(
  (acc, cfg) => (cfg.categoryId == null ? acc : { ...acc, [cfg.categoryId]: cfg }),
  {} as Record<number, GroupCategoryConfig>,
);

/**
 * Look up a group config by category slug (preferred) or numeric id.
 * Accepts either; returns null if neither matches.
 */
export const getGroupConfig = (
  identifier?: number | string | null,
): GroupCategoryConfig | null => {
  if (identifier == null) return null;
  if (typeof identifier === 'string') return BY_SLUG[identifier] || null;
  return GROUP_REGISTRY[identifier] || null;
};

export const getGroupConfigBySlug = (slug?: string | null): GroupCategoryConfig | null => {
  if (!slug) return null;
  return BY_SLUG[slug] || null;
};

/**
 * Accepts a category-like object ({id, slug}) or a bare id/slug. Returns
 * true when the category has groups. Pages that have access to the full
 * categoryInformation object should pass it whole so slug-based lookup
 * works for the categories whose ids we don't hardcode.
 */
export const isGroupCategory = (
  category?: number | string | { id?: number; slug?: string; is_custom?: boolean } | null,
): boolean => {
  if (category == null) return false;
  if (typeof category === 'string') return !!BY_SLUG[category];
  if (typeof category === 'number') return category in GROUP_REGISTRY;
  if (typeof category === 'object') {
    if (category.is_custom) return true;
    if (category.slug && BY_SLUG[category.slug]) return true;
    if (category.id != null && category.id in GROUP_REGISTRY) return true;
  }
  return false;
};

/** Returns the config that matches the given category, looking up by slug
 *  first (works for all 13) and falling back to id (vehicle/property only).
 *  User-created custom categories all share the customGroupConfig — their
 *  per-user slug never matches the registry, so we detect them via the
 *  `is_custom` flag the categories endpoint returns. */
export const getGroupConfigForCategory = (
  category?: { id?: number; slug?: string; is_custom?: boolean } | null,
): GroupCategoryConfig | null => {
  if (!category) return null;
  if (category.is_custom) return BY_SLUG.custom || null;
  if (category.slug) {
    const bySlug = BY_SLUG[category.slug];
    if (bySlug) return bySlug;
  }
  if (category.id != null) return GROUP_REGISTRY[category.id] || null;
  return null;
};

/** Look up config by the URL route segment used in /documents/viewGroup/:type/:id.
 *  Vehicle/property use their slug literally; the other 11 use apiPathSingular
 *  (or the explicit groupTypeForRoute override). */
export const getGroupConfigByRouteSegment = (segment?: string | null): GroupCategoryConfig | null => {
  if (!segment) return null;
  // Vehicle and property route by slug.
  if (segment === 'vehicle') return vehicleConfig;
  if (segment === 'property') return propertyConfig;
  // Everything else routes by apiPathSingular.
  const match = ALL_CONFIGS.find((c) => c.apiPathSingular === segment);
  return match || null;
};
